import classNames from "classnames";
import React, { useState, useMemo, useEffect, useRef, useCallback, memo, useId } from "react";

import { useTranslate } from "../../../../i18n/useTranslationModules";
import { useAppDispatch, useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import { QuestionType, RegistrationStepType, RegistrationTypes, RegFieldsEnum, SingleSignOnTypes, ISSOIntegration, ContentEditors, ChannelFeaturesEnum, RegistrationStep, LanguagesAbbr } from "../../../../types/working-model";
import { arrayToOptions, getRandomValue, isValidEmail, getTemplateClassName } from "../../../../utils/utils";
import { Button } from "../../../general-ui/button/button";
import CustomCheckbox from "../../../general-ui/checkbox/custom-checkbox";
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import SelectNative from "../../../general-ui/select/select-native";
import TextInput, { Validation } from "../../../general-ui/text-input/text";
import Textarea from "../../../general-ui/textarea/textarea";
import { TypographyItem } from "../../../general-ui/typography-item/typography-item";
import { ContentProps, IRegistrationFormField, SubmissionValues } from "../registration";
import { RegistrationFooter } from "../registration-footer";
import { showAlert } from "../../../general-ui/alert/alert-service";
import MultiSelect from "../../../general-ui/select/multi-select";
import { OptionalComponent } from "../../../../utils/optional-component";
import { getStorageItem, setStorageItem } from "../../../../utils/local-storage";
import WaitingIndicator from "../../../general-ui/waiting-indicator/waiting-indicator";
import { getCustomSignInCode, getSSOSignOnUrl, hasAnyConfiguredCustomSSO, isCustomSignOnType, isUsingCustomSSO } from "../../../../utils/sso-utils";
import { GetHv, parseHvPath, HvHostMap } from "../../../../connection/helpers";
import { validate as validateUuid } from 'uuid';
import { OptInCheckBoxes } from "./opt-in-checkboxes";
import { getRegFieldLabel, resolveInPersonYesValue } from "./utils";
import { getInPersonWorking, setInPersonWorking, isInPersonAnswerYes, IN_PERSON_YES } from "../../../../utils/in-person-intent";
import { getPendingPasscode, setPendingPasscode } from "../../../../utils/registration/pending-passcode";
import { selectInPersonAutoFill } from "../../../../store/selectors/feature-flags";
import { clearRegistrationError } from "../../../../store/actions/event/event-actions";
import { useFocusOnStepChange, useIsRegistrationV2 } from "hooks/registration-hooks";
import { RequiredFieldsLegend } from "../required-fields-legend";
import useIsStepSkippable from "./use-is-step-skippable";
import SignInWithGoogle from "../sign-in-with-google/sign-in-with-google";
import { checkPasswordGatingPasswordRequirement } from "components/live-event/utils";
import GeneralContentHeader from "./general-content-header";
import { RegFailureReasons, useGetRegistrationErrorMessage } from "@/utils/use-get-registration-error-message";
import { isValid } from "../../modules/products/use-buy-now-link";


// taken from https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
const RegFieldAutoComplete: Record<number, string> = {
	1: 'given-name',
	2: 'family-name',
	3: 'email',
	5: 'organization-title',
	6: 'organization',
	21: 'postal-code',
	38: 'tel'
};

const getGoogleSigninValues = () => {
	interface GoogleAuthUser {
		firstName: string;
		lastName: string;
		image: string;
		email: string;
		googleId: string;
		googleToken: string;
	}

	const params = new URLSearchParams(window.location.search);
	let defaultValues: Record<string | number, string> = {};
	for (const item of Array.from(params.entries())) {
		const [key, value] = item;

		//iterating over these rather than just checking the one key so we can expand on this later if we need to.
		if (key === "googleToken") {
			//handle google sign in success
			const user: GoogleAuthUser = {
				firstName: params.get('firstName') as string,
				lastName: params.get('lastName') as string,
				email: params.get('email') as string,
				image: params.get('image') as string,
				googleToken: value,
				googleId: params.get('googleId') as string
			};

			//delete their previous passcode sub
			delete defaultValues['10'];

			defaultValues = {
				...defaultValues,
				[RegFieldsEnum.first_name]: user.firstName ?? "",
				[RegFieldsEnum.last_name]: user.lastName ?? "",
				[RegFieldsEnum.email]: user.email,
				'avatar': user.image,
				'googleToken': user.googleToken
			};

			break;
		}

		if (key === "error") {
			defaultValues = {
				...defaultValues,
				error: params.get("details") ?? "popup"
			};
		}
	}

	//user has signed in with google, immedately remove the search params so that
	//we don't have issues when going to the profile page
	if (params.get('googleToken')) {
		window.history.replaceState({}, document.title, window.location.pathname);
	}

	return defaultValues;
};

//we only want to calculate this once on load. Hooks will fire this repeatedly and overwrite the values after they've been cleared
//this will make it so that it's set once on load only.
const signinValues = getGoogleSigninValues();

// new base language select field map should include the base language value of any item in a select field if it exists
export const getSubmissionWithBaseLanguageSelects = (
	values: SubmissionValues,
	selectBaseLanguageFieldMap: Map<string, string>,
	fields: IRegistrationFormField[]
) => {
	const _values = { ...values };
	for (const field of fields) {
		const value = _values[field.field_id];
		if (field.type === 'select' && typeof value === 'string') {
			// A value that is already base-language misses the translation->base map and would submit empty.
			const base = selectBaseLanguageFieldMap.get(value) ?? value;
			_values[`select_${field.field_id}`] = base;

			// Owned by the in-person toggle, which reads and writes base; the select_ sidecar is not persisted.
			if (field.field_id === RegFieldsEnum.attend_in_person) {
				_values[field.field_id] = base;
			}
		}
	}

	return _values;
};

export const GeneralContent = memo(({
	fields,
	template,
	onSaveData,
	footerProps,
	defaultValues,
	profile,
	channel,
	ticketing,
	handleSignIn,
	eventBundle,
	currentStep,
	language,
	renderRecaptcha,
	selectBaseLanguageFieldMap,
	gdprStatus,
	setIsMarketingOptInChecked,
	isMarketingOptInChecked,
	passwordGatingProps
}: ContentProps) => {
	const dispatch = useAppDispatch();
	const { t } = useTranslate(['registrations', 'homepage']);
	const headingId = useId();
	const fieldRefs = useRef<({ focus: () => void } | null)[]>([]);
	const firstFieldRef = useRef<{ focus: () => void } | null>(null);
	useFocusOnStepChange(firstFieldRef);

	const registerFieldRef = (index: number) => {
		return (el: { focus: () => void } | null) => {
			fieldRefs.current[index] = el;
			firstFieldRef.current = fieldRefs.current.find(Boolean) ?? null;
		};
	};


	const customSSOs = useTypedSelector(state => state.IntegrationsReducer.customSSOs);
	const registrationError = useTypedSelector(state => state.LiveEventReducer.registrationError);
	const numberOfTicketsPurchased = useTypedSelector(state => state.LiveEventReducer.numberOfTicketsPurchased);
	const registrationFieldLimitEnabled = useTypedSelector(state => state.LiveEventReducer.eventBundle?.channel_features?.includes(ChannelFeaturesEnum.platform_registration_field_limits));
	const errorMessage = useGetRegistrationErrorMessage(registrationError as RegFailureReasons);
	const translationUpdates = useTypedSelector(event => event.LiveEventReducer.translationUpdates);

	const hidePasswordConfirmationField = useMemo(() => getStorageItem('hide-pw-confirmation-field') === 'true', []);
	const customSignInCodeRef = useRef<string | null>(null);
	useEffect(() => {
		const code = getCustomSignInCode();
		customSignInCodeRef.current = code;
	}, []);
	const isRegistrationV2 = useIsRegistrationV2();

	const inPersonAutoFillEnabled = useTypedSelector(selectInPersonAutoFill);

	// Must seed in the initializer: SelectNative is uncontrolled and only reads defaultValue at mount.
	const inPersonSeed: Record<string, string> | undefined = useMemo(() => {
		if (!inPersonAutoFillEnabled) return undefined;
		if (!eventBundle?.registration_settings?.adminInPersonAttendeeModeEnabled) return undefined;

		// The stored record is primary; the profile answer only covers a device that has none.
		// Never read defaultValues here — this question has a default_value, so it is always present.
		const isInPerson = getInPersonWorking(eventBundle?.uuid) ?? isInPersonAnswerYes(profile?.profile?.[channel]);
		const yesValue = resolveInPersonYesValue(fields, selectBaseLanguageFieldMap);

		if (!isInPerson || !yesValue) return undefined;
		return { [RegFieldsEnum.attend_in_person]: yesValue };

		// includes translationUpdates
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [translationUpdates]);

	const [fieldValues, setFieldValues] = useState<SubmissionValues>({
		...defaultValues,
		...signinValues,
		...inPersonSeed,
		// Google SSO + passcode (without this code) refreshes the page and the user has to retype the passcode.
		[RegFieldsEnum.passcode]: signinValues.googleToken && currentStep?.type === RegistrationStepType.general
			? getPendingPasscode(eventBundle?.uuid)
			: undefined
	});

	const [invalidFields, setInvalidFields] = useState<number[]>([]);
	const [soldOut, setSoldOut] = useState<boolean>(false);
	const [autoRegistering, setAutoRegistering] = useState(false);
	const hasAutoRegistered = useRef(false);
	const [fetchedSSO, setFetchedSSO] = useState(null);
	const invalidFieldsRef = useRef(invalidFields);
	invalidFieldsRef.current = invalidFields;

	const ssoErrors = fieldValues.error;
	const profileValues = profile?.profile?.[channel];
	const isRegClosed = !!eventBundle?.registration_settings?.isClosed;
	const ssoSettings = eventBundle?.registration_settings?.singleSignOn;
	const cookiesEnabled = !!eventBundle?.settings.gdpr?.enable_cookie_banner;
	const cookieOptIn = !!eventBundle?.channel_features?.includes(ChannelFeaturesEnum.cookie_opt_in);
	const externalRegistrationEnabled = !!eventBundle?.registration_settings?.externalRegistration?.isOn;

	const signedInWithGoogle = useMemo(() => !!signinValues.googleToken, []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const signedInWithCustomLogin = useMemo(() => !!customSignInCodeRef.current, [customSignInCodeRef.current]);
	const passwordGatingEnabled = useMemo(() => !!eventBundle?.registration_settings?.password_gating_enabled, [eventBundle?.registration_settings?.password_gating_enabled]);
	const requiredFieldIds = useMemo(() => (fields
		.filter(field => field.required)
		.map(field => field.field_id)
	), [fields]);

	const isSkippable = useIsStepSkippable(
		currentStep?.type,
		eventBundle?.registration_steps,
		requiredFieldIds,
	);

	useEffect(() => {
		if (ssoErrors && !isUsingCustomSSO(eventBundle ?? null)) {
			const _ssoErrors = Array.isArray(ssoErrors) ? ssoErrors.join(', ') : String(ssoErrors);
			showAlert({
				message: t("homepage:registration.Google Sign in Error"),
				description: ssoErrors === "popup" ? t("homepage:registration.closed_popup") : _ssoErrors,
				duration: 7000,
				type: "error"
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ssoErrors]);

	// fieldValues needs to be updated with the defaultValues once the general step is passed
	useEffect(() => setFieldValues((prev) => ({
		...defaultValues,
		...prev,
		// This is to prevent a very specific case where the email field and token get out of sync
		// which prevents registration.
		...(defaultValues[RegFieldsEnum.email] ? { 3: defaultValues[RegFieldsEnum.email] } : {})
	})), [defaultValues]);

	useEffect(() => {
		// fields changed, so we need to ensure that the invalid fields have been reset
		const invalidRefSet = new Set(invalidFieldsRef.current);
		const fieldsPresent = new Set(fields.map(field => field.field_id));

		// only keep the invalid fields that are present in this view
		setInvalidFields(Array.from(invalidRefSet.intersection(fieldsPresent)));
	}, [fields]);

	// defaultValue already follows the retranslated seed; this keeps the submitted value in step with it.
	const seededInPersonValue = inPersonSeed?.[RegFieldsEnum.attend_in_person];

	useEffect(() => {
		if (typeof seededInPersonValue !== 'string') return;

		setFieldValues(prev => prev[RegFieldsEnum.attend_in_person] === seededInPersonValue
			? prev
			: { ...prev, [RegFieldsEnum.attend_in_person]: seededInPersonValue });
	}, [seededInPersonValue]);

	useEffect(() => {
		if (!ticketing || !numberOfTicketsPurchased || Object.keys(numberOfTicketsPurchased).length === 0) {
			return;
		}

		// If ALL tickets have quantity <= the tickets purchased for that ticket, then the event is sold out
		const _soldOut = ticketing.map(ticket => {
			return ticket.quantity <= (numberOfTicketsPurchased[ticket.uuid] || 0);
		}).reduce((prev: boolean, current: boolean) => prev && current, true);

		setSoldOut(_soldOut);
	}, [numberOfTicketsPurchased, ticketing]);

	const setSelectValue = (field: number) => {
		return ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
			//pull field by id
			const _field = fields.find(_field => _field.field_id === field);

			//check if entry is valid (if _field doesn't exist, don't break registration, let it through)
			const isValid = _field ? validateField(_field, value) : true;

			//hold ids of invalid fields
			setInvalidFields((fields) => {
				if (isValid) {
					return fields.filter(_field => _field !== field);
				} else {
					return Array.from(new Set([...fields, field]).values());
				}
			});

			//set values for submission
			setFieldValues((values) => {
				values[field] = value;
				return { ...values };
			});

			if (field === RegFieldsEnum.attend_in_person) {
				setInPersonWorking(
					eventBundle?.uuid,
					(selectBaseLanguageFieldMap.get(value) ?? value) === IN_PERSON_YES
				);
			}
		};
	};

	const setValue = (field: number) => {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			//pull field by id
			const _field = fields.find(_field => _field.field_id === field);

			//check if entry is valid (if _field doesn't exist, don't break registration, let it through)
			const isValid = _field ? validateField(_field, value) : true;

			//hold ids of invalid fields
			setInvalidFields((fields) => {
				if (isValid) {
					return fields.filter(_field => _field !== field);
				} else {
					return Array.from(new Set([...fields, field]).values());
				}
			});

			//set values for submission
			setFieldValues((values) => {
				values[field] = value;
				return { ...values };
			});
		};
	};

	//checkbox returns different event type than text/select fields, needs different handler
	const setCheckboxValue = (field: number) => {
		return (isChecked: boolean) => {
			//pull field by id
			const _field = fields.find(_field => _field.field_id === field);

			//if checkbox is required, user must check it to pass
			setInvalidFields((fields) => {
				if (_field?.required && !isChecked) {
					return Array.from(new Set([...fields, field]).values());
				} else {
					return fields.filter(_field => _field !== field);
				}
			});

			//set values for submission
			setFieldValues((values) => {
				values[field] = isChecked;
				return { ...values };
			});
		};
	};

	// clear error message on unmount so we don't show it when navigating away and back to the page
	useEffect(() => {
		return () => {
			dispatch(clearRegistrationError());
		};
	}, [dispatch]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		dispatch(clearRegistrationError()); // clear error on submit so we get some sort of user feedback if an error occurs again

		const values = fieldValues;

		// prevent submit and show errors for missing required info, event sold out, and no ticket selected
		const missingRequired = requiredFieldIds.filter(id => {
			const val = values?.[id];
			if (val && Array.isArray(val)) {
				return !val.length;
			} else {
				return !val;
			}
		});

		const nextInvalidFields = [...invalidFields, ...missingRequired];
		setInvalidFields(nextInvalidFields);
		if (soldOut || missingRequired.length) {
			// submit disables once fields are invalid, so focus would drop to the page
			const firstInvalidIndex = fields.findIndex(field => nextInvalidFields.includes(field.field_id));
			fieldRefs.current[firstInvalidIndex]?.focus();
			return false;
		}

		onSaveData(getSubmissionWithBaseLanguageSelects(values, selectBaseLanguageFieldMap, fields));
	};

	const canReRegister = useMemo(() => {
		return fields.some((field) => field.field_id === RegFieldsEnum.email);
	}, [fields]);

	const renderInvalidField = (field: IRegistrationFormField): JSX.Element => {
		if (soldOut) return <></>; // no need to show error messages if form is sold out
		if (invalidFields.includes(field.field_id)) {
			return (
				field.type === QuestionType.email ? (
					<p className="registration-error" role="alert">{t("homepage:registration.Please enter a valid email address")}</p>
				) : (
					<p className="registration-error" role="alert">{t("homepage:registration.Field is required")}</p>
				)
			);
		}
		return <></>;
	};

	const handleMultiSelectChange = (value: any, _meta: any, field: IRegistrationFormField) => {
		//hold ids of invalid fields
		const isValid = !field?.required || (field?.required && value?.length);
		setInvalidFields((fields: any) => {
			if (isValid) {
				return fields.filter((_field: any) => _field !== field.field_id);
			} else {
				return Array.from(new Set([...fields, field.field_id]).values());
			}
		});

		//set values for submission
		setFieldValues((prev) => {
			return {
				...prev,
				[field.field_id]: value?.map?.((val: any) => val.value) ?? [],
			};
		});
	};

	const renderField = (field: IRegistrationFormField, index: number) => {
		if (!eventBundle || !field) return null;
		switch (field.type) {
			//these are all text fields
			case QuestionType.email:
			case QuestionType.numeric:
			case QuestionType.text: {
				const hidePasscode = field.field_id === RegFieldsEnum.passcode &&
					eventBundle?.registration_settings?.type !== RegistrationTypes.gated &&
					!eventBundle?.settings.pageGating?.isOn;

				if (hidePasscode) {
					return null;
				}

				const [translatedLabel, translatedPlaceHolder] = getRegFieldLabel({
					t,
					eventBundle,
					field,
					language,
					eventRegistrationTranslations: eventBundle?.registration_settings?.registration_questions_translations,
				});
				return (
					<>
						<TextInput
							ref={registerFieldRef(index)}
							label={translatedLabel}
							placeholder={translatedPlaceHolder}
							required={field.required}
							defaultValue={fieldValues[field.field_id] as string ?? profileValues?.[field.field_id]}
							onChange={setValue(field.field_id)}
							email={field.type === QuestionType.email}
							numeric={field.type === QuestionType.numeric}
							name={field.field_id.toString()}
							valid={invalidFields.includes(field.field_id) ? Validation.error : (fieldValues[field.field_id] ? Validation.ok : Validation.normal)}
							disabled={soldOut}
							autoComplete={RegFieldAutoComplete[field.field_id]}
							maxLength={registrationFieldLimitEnabled ? field.settings?.max_length : undefined}
							showMaxLength
							language={language}
						/>
						<OptionalComponent display={field.field_id === RegFieldsEnum.email && passwordGatingEnabled}>
							{/* The invalid email error message has to be moved above the password fields */}
							{renderInvalidField(field)}
							<TextInput
								id={`question-text-${field.field_id}-password`}
								key={field.field_id + '-password'}
								required
								label={
									hidePasswordConfirmationField ?
										t('homepage:registration.registration_fields.Password', 'Password') :
										t('homepage:registration.registration_fields.Set password', 'Set password')}
								placeholder={
									hidePasswordConfirmationField ?
										t('homepage:registration.registration_fields.Password', 'Password') :
										t('homepage:registration.registration_fields.Set password', 'Set password')
								}
								onChange={e => {
									passwordGatingProps?.setPassword(e.target.value);

									// this user has previously set a password so we don't need to show the confirmation field
									// as we are treating this as a sign in rather than a sign up. The password will still
									// be checked.
									if (hidePasswordConfirmationField) {
										passwordGatingProps?.setPasswordConfirmation(e.target.value);
									}
								}}
								password
								showPasswordIcon
								autoCorrect="off"
								autoCapitalize="off"
							/>
							<OptionalComponent display={!!passwordGatingProps?.password && !checkPasswordGatingPasswordRequirement(passwordGatingProps.password)}>
								<p className="registration-error" role="alert">{t("homepage:registration.Password Requirement", 'Password must be at least 8 characters long.')}</p>
							</OptionalComponent>
							<OptionalComponent display={!hidePasswordConfirmationField}>
								<TextInput
									id={`question-text-${field.field_id}-confirm-password`}
									key={field.field_id + 'confirm-password'}
									required
									label={t('homepage:registration.registration_fields.Confirm password', 'Confirm password')}
									placeholder={t('homepage:registration.registration_fields.Confirm password', 'Confirm password')}
									onChange={e => passwordGatingProps?.setPasswordConfirmation(e.target.value)}
									valid={passwordGatingProps?.passwordConfirmation.length && passwordGatingProps?.password !== passwordGatingProps?.passwordConfirmation ? Validation.error : Validation.normal}
									password
									showPasswordIcon
									autoCorrect="off"
									autoCapitalize="off"
								/>
							</OptionalComponent>
						</OptionalComponent>
					</>
				);
			}
			case QuestionType.checkbox: {
				return (
					<CustomCheckbox
						ref={registerFieldRef(index)}
						key={field.field_id}
						label={field.label}
						onChange={setCheckboxValue(field.field_id)}
						defaultValue={fieldValues[field.field_id] ?? profileValues?.[field.field_id]}
						checked={!!fieldValues[field.field_id]}
						name={field.field_id.toString()}
						disabled={soldOut}
						useMarkdown
					/>
				);
			}
			case QuestionType.select: {
				return (
					<SelectNative
						ref={registerFieldRef(index)}
						key={translationUpdates}
						options={field.options}
						label={field.label}
						required={field.required}
						defaultValue={inPersonSeed?.[field.field_id] ?? fieldValues[field.field_id] ?? profileValues?.[field.field_id] ?? ''}
						onChange={setSelectValue(field.field_id)}
						name={field.field_id.toString()}
						placeholder={field.placeholder}
						disabled={soldOut}
					/>
				);
			}
			case QuestionType.multiselect: {
				return (
					<MultiSelect
						ref={registerFieldRef(index)}
						defaultValue={arrayToOptions(fieldValues[field.field_id] ?? profileValues?.[field.field_id] ?? [])}
						options={field.options ?? []}
						onChange={handleMultiSelectChange}
						template={isRegistrationV2 ? '' : getTemplateClassName(template)}
						label={field.label}
						field={field}
						placeholder={field.placeholder}
						disabled={soldOut}
					/>
				);
			}
			case QuestionType.textarea: {
				return (
					<Textarea
						ref={registerFieldRef(index)}
						label={field.label}
						placeholder={field.placeholder}
						required={field.required}
						defaultValue={fieldValues[field.field_id] ?? profileValues?.[field.field_id]}
						onChange={setValue(field.field_id)}
						name={field.field_id.toString()}
						valid={invalidFields.includes(field.field_id) ? Validation.error : (fieldValues[field.field_id] ? Validation.ok : Validation.normal)}
						disabled={soldOut}
						maxLength={registrationFieldLimitEnabled ? field.settings?.max_length : undefined}
						showMaxLength
						language={language}
					/>
				);
			}
			case QuestionType.customtext: {
				return (
					<TypographyItem className={classNames("evt-field-label", {
						'is-quill': eventBundle?.settings?.content_editor === ContentEditors.quill
					})}>
						<RenderEditorDescription
							description={field.label || ''}
							useNew={eventBundle?.settings?.content_editor === ContentEditors.quill}
						/>
					</TypographyItem>
				);
			}
			default: {
				return null;
			}
		}
	};

	const cookieConsentBlocking = !cookieOptIn && cookiesEnabled && !gdprStatus?.cookies_accepted;

	useEffect(() => {
		if (signedInWithGoogle && !autoRegistering && !hasAutoRegistered.current && !cookieConsentBlocking && currentStep?.type === RegistrationStepType.general && currentStep?.isOn && !registrationError) {
			hasAutoRegistered.current = true;
			setAutoRegistering(true);
			const values = fieldValues;

			// prevent submit and show errors for missing required info, event sold out, and no ticket selected
			// also ignore basic profile fields first and last name if they're not defined from the google profile
			const missingRequired = requiredFieldIds.filter(id => id > 2).filter(id => {
				const val = values?.[id];
				if (val && Array.isArray(val)) {
					return !val.length;
				} else {
					return !val;
				}
			});
			setInvalidFields(invalidFields => [...invalidFields, ...missingRequired]);
			if (!missingRequired.length) {
				// Same base-language selects as handleSubmit: without them a translated answer is stored untranslated.
				onSaveData(getSubmissionWithBaseLanguageSelects(values, selectBaseLanguageFieldMap, fields));
			}
		}
		if (signedInWithCustomLogin && !autoRegistering && !hasAutoRegistered.current && !cookieConsentBlocking && currentStep?.type === RegistrationStepType.general && currentStep?.isOn && !registrationError && customSignInCodeRef.current) {
			hasAutoRegistered.current = true;
			setAutoRegistering(true);
			onSaveData(getSubmissionWithBaseLanguageSelects(fieldValues, selectBaseLanguageFieldMap, fields), customSignInCodeRef.current);
		}
		// if autoRegistering is in dependency array then we get an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		fieldValues,
		requiredFieldIds,
		signedInWithGoogle,
		soldOut,
		onSaveData,
		currentStep,
		signedInWithCustomLogin,
		registrationError,
		cookieConsentBlocking,
		translationUpdates,
	]);

	// if there is an error auto registering, then set it to false and display the error
	useEffect(() => {
		if (autoRegistering && errorMessage) {
			setAutoRegistering(false);

			// social sign-ins already render the inline error; the alert is custom-SSO only
			if (!isUsingCustomSSO(eventBundle ?? null)) return;

			const showDescription = errorMessage === t("homepage:registration.SSO Sign in Error");
			const isNotString = typeof errorMessage !== "string";

			showAlert({
				message: isNotString ? undefined : errorMessage,
				children: isNotString ? errorMessage : undefined,
				description: showDescription ? t("homepage:registration.Please try again") : undefined,
				duration: 7000,
				type: "error"
			});
		}
		// if autoRegistering is in dependency array then we get an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorMessage]);

	const passcodeValue = fieldValues[RegFieldsEnum.passcode];

	const renderSSOOption = useCallback((signOn: string) => {
		if (signOn === SingleSignOnTypes.Google) {
			return (
				<SignInWithGoogle
					key={signOn}
					isRegClosed={isRegClosed}
					autoRegistering={autoRegistering}
					soldOut={soldOut}
					onBeforeRedirect={() => setPendingPasscode(
						eventBundle?.uuid,
						typeof passcodeValue === 'string' ? passcodeValue : ''
					)}
				/>
			);
		} else {
			return null;
		}
	}, [autoRegistering, isRegClosed, soldOut, passcodeValue, eventBundle?.uuid]);

	// we use the same reg step component for general and profile steps. Need to include the !profile step case here to make sure
	// sso doesn't appear on that step as well
	const displaySSO = ssoSettings?.isOn && ssoSettings?.singleSignOns?.length && currentStep?.type === RegistrationStepType.general;
	const useCustomLogin = isUsingCustomSSO(eventBundle ?? null);
	const ssoArray = eventBundle?.registration_settings?.singleSignOn?.singleSignOns;
	// in preview mode the customSSOs will be in redux, in a live event we get the sso from the channel settings in the event bundle
	const customSSO: ISSOIntegration | undefined = ssoArray && (customSSOs ?
		customSSOs?.find((sso: ISSOIntegration) => sso.uuid === ssoArray[0]) :
		fetchedSSO ?? eventBundle?.channel_settings?.sso?.[ssoArray[0]]);

	// Fetch latest version of SSO in case the event hasn't been published since being updated
	useEffect(() => {
		const getSSO = async (uuid: string) => {
			try {
				const response = await GetHv(HvHostMap.authorization, `/auth/getSSO/${uuid}`);

				setFetchedSSO(response);
			} catch (e) {
				console.error(`Failed to fetch SSO ${uuid}. Error: `, e);
			}
		};

		if (useCustomLogin && ssoArray?.length) {
			const uuid = ssoArray[0]; // Limited to one sso per event;

			if (validateUuid(uuid)) {
				getSSO(uuid);
			}
		}

	}, [ssoArray, useCustomLogin]);

	const publicOauthIntegration = eventBundle?.channel_settings?.oauth2Integration;
	const ssoButtonText = (customSSO ? customSSO?.login_text : publicOauthIntegration?.login_text) ?? "";
	const ssoButtonImage = (customSSO ? customSSO?.icon : publicOauthIntegration?.icon) ?? "";


	const redirectToOAuth2SignIn = async () => {
		const state = getRandomValue();
		setStorageItem(`sso_state`, state, 1);

		const params = new URLSearchParams({ redirect_uri: `${location.origin}/sso-redirect`, state });

		window.location.href = await parseHvPath(HvHostMap.registration, `/e3-oauth-sign-in/${eventBundle?.channel}?${params}`);
	};

	const handleExternalRegLink = () => {
		if (!eventBundle?.registration_settings?.externalRegistration?.url) return;
		window.open(eventBundle.registration_settings?.externalRegistration?.url, '_target');
	};

	const nextDisabled = useMemo(() => {
		if (soldOut) {
			return true;
		}

		if (passwordGatingEnabled) {
			// one or the other password or password confirmation is empty
			if (!passwordGatingProps?.password || !passwordGatingProps?.passwordConfirmation) {
				return true;
			}

			// password does not match confirmation
			if (passwordGatingProps?.password !== passwordGatingProps?.passwordConfirmation) {
				return true;
			}

			// password does not meet requirements
			return !!passwordGatingProps?.password && !checkPasswordGatingPasswordRequirement(passwordGatingProps.password);
		}

		return false;
	}, [soldOut, passwordGatingProps?.password, passwordGatingProps?.passwordConfirmation, passwordGatingEnabled]);

	return (
		<form
			className="registration-form"
			onSubmit={handleSubmit}
			aria-labelledby={headingId}
		>
			<fieldset disabled={!cookieOptIn && cookiesEnabled && !gdprStatus} className="clear-fieldset">
				<div className="registration-form-inner">
					<TypographyItem className={classNames("evt-heading-2 stable registration-form-title", currentStep?.styling_override?.color)} tagName="h2" id={headingId}>
						<GeneralContentHeader soldOut={soldOut} language={language} currentStep={currentStep} />
					</TypographyItem>
					<OptionalComponent display={requiredFieldIds.length > 0}>
						<RequiredFieldsLegend />
					</OptionalComponent>
					{useCustomLogin && displaySSO ? (
						<div>
							<div className="button-icon-wrapper">
								<Button
									typeBtn="button"
									classButton={classNames("evt-button full-width primary negative google-signin", getTemplateClassName(template))}
									onClick={() => {
										// set an expiration so if the user for some reason does not go through the flow and they
										// end up on a different event at a later time, they don't get redirected to the wrong event
										// 0.17 is about 10 minutes converted to hours
										if (eventBundle?.uuid && useCustomLogin) {
											setStorageItem(`sso_event_url`, window.location.href, 1);

											if (customSSO) {
												window.location.href = getSSOSignOnUrl(customSSO);
											} else if (ssoArray?.some(isCustomSignOnType) && publicOauthIntegration) {
												redirectToOAuth2SignIn();
											}
										}
									}}
									isDisabled={autoRegistering || soldOut || isRegClosed}
								>
									{autoRegistering ? <WaitingIndicator /> : (
										<>
											<OptionalComponent display={!!ssoButtonImage}>
												<img alt="Sign-in with SSO buttton image" src={ssoButtonImage} />
											</OptionalComponent>

											<OptionalComponent display={!!isRegClosed}>
												<>{t("homepage:registration.Registration is closed")}</>
											</OptionalComponent>

											<OptionalComponent display={!isRegClosed}>
												<>{t("homepage:registration.SignInWith")} {ssoButtonText}</>
											</OptionalComponent>
										</>
									)}
								</Button>
							</div>
						</div>
					) : (
						<>
							{externalRegistrationEnabled && !!eventBundle.registration_settings?.externalRegistration?.url && (
								<button className="registration-button primary external-registration" onClick={handleExternalRegLink}>{t("homepage:registration.Register")}</button>
							)}
							{canReRegister && (
								<button onClick={handleSignIn} role="button" type="button" className="no-style sign-in already-signed-in">{t("homepage:registration.AlreadyRegistered")}</button>
							)}

							{soldOut && (<p className="sold-out">{t('homepage:registration.soldOutSubHeader')}</p>)}

							{!externalRegistrationEnabled && (
								<div className={classNames(soldOut ? 'sold-out-container' : '')}>
									{displaySSO && ssoSettings?.displayTop ? (
										<div className='sso-container'>
											{ssoSettings?.singleSignOns?.map(renderSSOOption)}
											<div className="sso-sign-in-text">- {t('homepage:registration.or register with email')} -</div>
										</div>
									) : null}
									{fields.map((field: IRegistrationFormField, index) => {
										return (
											<div key={index + field.label + field.field_id} className={classNames('evt-field-wrapper')} id={`registration-field-${field.field_id}`}>
												{renderField(field, index)}
												<OptionalComponent display={!(field.field_id === RegFieldsEnum.email && passwordGatingEnabled)}>
													{/* Email and password fields get grouped together so have to move the email error message in this case */}
													{renderInvalidField(field)}
												</OptionalComponent>
											</div>
										);
									})}

									{setIsMarketingOptInChecked && typeof isMarketingOptInChecked !== 'undefined' && (
										<OptInCheckBoxes
											setIsMarketingOptInChecked={setIsMarketingOptInChecked}
											template={''}
											soldOut={soldOut}
											isMarketingOptInChecked={isMarketingOptInChecked}
										/>
									)}

									{displaySSO && !ssoSettings?.displayTop ? (
										<div className='sso-container'>
											<div className="sso-sign-in-text">- {t('homepage:registration.or sign in with')} -</div>
											{ssoSettings?.singleSignOns?.map(renderSSOOption)}
										</div>
									) : null}
								</div>)}
						</>
					)}
				</div>

				{!externalRegistrationEnabled && (
					<RegistrationFooter
						{...footerProps}
						isNextDisabled={nextDisabled}
						isSkippable={isSkippable}
						waiting={footerProps.waiting || invalidFields.length > 0 || soldOut}
						template={getTemplateClassName(template)}
						renderRecaptcha={renderRecaptcha}
						useCustomLogin={hasAnyConfiguredCustomSSO(eventBundle ?? null)}
					/>)}
			</fieldset>
		</form>
	);
});

function validateField(field: IRegistrationFormField, value: string): boolean {
	if (field.field_id === RegFieldsEnum.email) {
		// if email is not required, allow empty field only, else run validation as normal
		if (!field.required && value === "") {
			return true;
		}
		return isValidEmail(value);
	}

	if (field.required) {
		return !!value;
	} else {
		return true;
	}
}
